import React, { useState, useEffect } from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Modal,
  TextField,
  Typography,
  Box,
  Tabs,
  Tab,
  MenuItem,
  Select,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import axios from "../../../axiosInstance";
import "./PriceManagement.css"; // Import external CSS file
import Sidebar_admin from "../Sidebar_Admin/Sidebar_admin";


const PriceManagement = () => {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [activeTab, setActiveTab] = useState(0); // 0 for categories, 1 for items
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const { register, handleSubmit, reset, setValue } = useForm();

  // Fetch categories and items on component mount
  useEffect(() => {
    fetchCategories();
    fetchItems();
  }, []);

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const response = await axios.get(`/api/admin/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Fetch items
  const fetchItems = async () => {
    try {
      const response = await axios.get(`/api/admin/items`);
      setItems(response.data);
    } catch (error) {
      console.error("Error fetching items:", error);
    }
  };

  // Open Modal
  const openModal = (item = null) => {
    setEditItem(item);
    reset(
      item
        ? { ...item }
        : activeTab === 0
        ? { name: "" } // For categories
        : { name: "", category: "", price: "" } // For items
    );
    setModalOpen(true);
  };

  // Close Modal
  const closeModal = () => setModalOpen(false);

  // Handle Form Submit
  const onSubmit = async (data) => {
    if (activeTab === 0) {
      // Manage Categories
      if (editItem) {
        // Update category
        try {
          await axios.put(`/api/admin/categories/${editItem._id}`, data);
          fetchCategories();
        } catch (error) {
          console.error("Error updating category:", error);
        }
      } else {
        // Add new category
        try {
          await axios.post(`/api/admin/categories`, data);
          fetchCategories();
        } catch (error) {
          console.error("Error adding category:", error);
        }
      }
    } else {
      // Manage Items
      if (editItem) {
        // Update item
        try {
          await axios.put(`/api/admin/items/${editItem._id}`, data);
          fetchItems();
        } catch (error) {
          console.error("Error updating item:", error);
        }
      } else {
        // Add new item
        try {
          await axios.post(`/api/admin/items`, data);
          fetchItems();
        } catch (error) {
          console.error("Error adding item:", error);
        }
      }
    }
    closeModal();
  };

  // Delete Item/Category
  const deleteItem = async (id) => {
    if (activeTab === 0) {
      try {
        await axios.delete(`/api/admin/categories/${id}`);
        fetchCategories();
      } catch (error) {
        console.error("Error deleting category:", error);
      }
    } else {
      try {
        await axios.delete(`/api/admin/items/${id}`);
        fetchItems();
      } catch (error) {
        console.error("Error deleting item:", error);
      }
    }
  };

  return (
    <div>
      <Sidebar_admin />
      <Box className="price-management-container">
        <Typography variant="h4" gutterBottom>
          Price Management
        </Typography>

        {/* Tabs for switching between Categories and Items */}
        <Tabs value={activeTab} onChange={(e, value) => setActiveTab(value)} className="tabs">
          <Tab label="Categories" />
          <Tab label="Items" />
        </Tabs>

        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => openModal()}
          className="add-button"
        >
          {activeTab === 0 ? "Add Category" : "Add Item"}
        </Button>

        {/* Table for Categories/Items */}
        <TableContainer component={Paper} className="table-container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                {activeTab === 1 && <TableCell>Category</TableCell>}
                {activeTab === 1 && <TableCell>Price</TableCell>}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(activeTab === 0 ? categories : items).map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  {activeTab === 1 && <TableCell>{item.category}</TableCell>}
                  {activeTab === 1 && (
                    <TableCell>
                      ₹{item.price.toLocaleString()} {/* Add INR symbol */}
                    </TableCell>
                  )}
                  <TableCell>
                    <IconButton onClick={() => openModal(item)} color="primary">
                      <Edit />
                    </IconButton>
                    <IconButton onClick={() => deleteItem(item._id)} color="error">
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Modal for Add/Edit */}
        <Modal open={modalOpen} onClose={closeModal}>
          <Box className="modal-container">
            <Typography variant="h6" gutterBottom>
              {editItem
                ? activeTab === 0
                  ? "Edit Category"
                  : "Edit Item"
                : activeTab === 0
                ? "Add New Category"
                : "Add New Item"}
            </Typography>
            <form onSubmit={handleSubmit(onSubmit)}>
              <TextField
                label="Name"
                fullWidth
                variant="outlined"
                margin="normal"
                {...register("name", { required: "Name is required" })}
              />
              {activeTab === 1 && (
                <>
                  <Select
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    {...register("category", { required: "Category is required" })}
                    defaultValue={editItem?.category || ""}
                    onChange={(e) => setValue("category", e.target.value)}
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.name}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <TextField
                    label="Price"
                    type="text"
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    {...register("price", { required: "Price is required" })}
                  />
                </>
              )}
              <Box className="modal-buttons">
                <Button onClick={closeModal} color="secondary">
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="primary">
                  {editItem ? "Update" : "Add"}
                </Button>
              </Box>
            </form>
          </Box>
        </Modal>
      </Box>
    </div>
  );
};

export default PriceManagement;
